import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import ContentPage from "@/components/contentPage";
import { useState, useContext } from "react";
import Map from "@/components/map";
import { calcDateDuration, formatUtcDate } from "@/utils";
import { hasFeatureFlag } from "@/utils";
import UserContext from "@/contexts/user";
import { AvailableFeatureFlags } from "@/apiClient";

const MAP_PAGE_SIZE = 500;
const LIST_PAGE_SIZE = 500;

const realAssetDetailQueryOptions = (api: any, publicId: string) =>
  queryOptions({
    queryKey: ["realAssetDetail", publicId],
    queryFn: () => api.dataAccess.realAssetDetail({ publicId }),
    enabled: !!publicId,
    throwOnError: true,
    refetchOnWindowFocus: false,
  });

const realAssetDeploymentsQueryOptions = (api: any, publicId: string, variant: string, size: number) =>
  queryOptions({
    queryKey: ["realAssetDeployments", publicId, variant],
    queryFn: () =>
      api.dataAccess.listRealAssetDeployments({
        realAssetPublicId: [publicId],
        size,
      }),
    enabled: !!publicId,
    throwOnError: true,
    refetchOnWindowFocus: false,
  });

export const Route = createFileRoute("/realassets/$publicId/")({
  component: RealAssetDetail,
  loader: async ({ context: { queryClient, api }, params: { publicId } }) => {
    await Promise.all([
      queryClient.prefetchQuery(realAssetDetailQueryOptions(api, publicId)),
      queryClient.prefetchQuery(realAssetDeploymentsQueryOptions(api, publicId, "map", MAP_PAGE_SIZE)),
      queryClient.prefetchQuery(realAssetDeploymentsQueryOptions(api, publicId, "list", LIST_PAGE_SIZE)),
    ]);
  },
});

function RealAssetDetail() {
  const { api } = Route.useRouteContext();
  const { publicId } = Route.useParams();
  const [selectedDeploymentId] = useState([]);
  const [searchString, setSearchString] = useState("");
  const { user } = useContext(UserContext);

  const realAssetQuery = useQuery(realAssetDetailQueryOptions(api, publicId));

  const mapDeploymentsQuery = useQuery(
    realAssetDeploymentsQueryOptions(api, publicId, "map", MAP_PAGE_SIZE),
  );

  const fullDeploymentsQuery = useQuery(
    realAssetDeploymentsQueryOptions(api, publicId, "list", LIST_PAGE_SIZE),
  );

  if (
    realAssetQuery.isPending ||
    mapDeploymentsQuery.isPending ||
    fullDeploymentsQuery.isPending
  )
    return "Loading...";
  if (
    realAssetQuery.error ||
    mapDeploymentsQuery.error ||
    fullDeploymentsQuery.error
  )
    return "An error occurred";

  const filteredDeployments = fullDeploymentsQuery.data?.items?.filter(
    (deployment: any) => {
      const searchTerm = searchString.trim().toLowerCase();
      if (!searchTerm) return true;

      const nameIncludes = deployment.name
        ? deployment.name.toLowerCase().includes(searchTerm)
        : false;
      const descriptionIncludes = deployment.description
        ? deployment.description.toLowerCase().includes(searchTerm)
        : false;
      const externalIdIncludes = deployment.external_id
        ? deployment.external_id.toString().toLowerCase().includes(searchTerm)
        : false;
      const combinedIncludes = `${deployment.name} | ${deployment.external_id}`
        .toLowerCase()
        .includes(searchTerm);

      return (
        nameIncludes ||
        descriptionIncludes ||
        externalIdIncludes ||
        combinedIncludes
      );
    }
  );

  const deploymentsCount = filteredDeployments?.length || 0;

  const hasDeploymentFeature = hasFeatureFlag(
    user,
    AvailableFeatureFlags.REAL_ASSET_DEPLOYMENT_DETAIL
  );

  return (
    <ContentPage title={realAssetQuery.data.name}>
      <>
        <div className={"relative h-3/4 max-h-[32.1875rem] w-full shadow-md"}>
          <Map
            deployments={mapDeploymentsQuery.data?.items}
            selectedDeploymentIds={selectedDeploymentId}
            currentPageProp={mapDeploymentsQuery.data.page}
            pageSizeProp={mapDeploymentsQuery.data.size}
            mapLocation={null}
            pinnedEventDetail={null}
            timelineItemsById={{}}
            onEventSelect={() => {}}
            onEventHover={() => {}}
            onEventDeselect={() => {}}
          />
        </div>
        <div className="mt-8 flex w-full flex-col justify-center">
          <label className="w-1/2">
            <p className="mb-2 font-light text-blue-400">
              Filter deployments by name, ID, or description...
            </p>
            <input
              type="text"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              className="form-input mt-1 block w-full rounded text-blue-400"
              placeholder="Search deployments..."
            />
          </label>
        </div>
        <p className="mb-[0.75rem] mt-5">{realAssetQuery.data.description}</p>
        <p>Total Number of Deployments: {deploymentsCount}</p>

        <div className="grid grid-cols-1 gap-4 pt-2">
          {filteredDeployments?.map((deployment: any) => (
            <div key={deployment.public_id} className="border p-4">
              {hasDeploymentFeature ? (
                <Link
                  to={`/realassets/${publicId}/${deployment.public_id}` as any}
                >
                  <h1 className="text-lg font-bold text-blue-200 hover:cursor-pointer hover:underline">
                    {deployment.name.includes(deployment.external_id)
                      ? deployment.name
                      : `${deployment.name} | ${deployment.external_id}`}
                  </h1>
                </Link>
              ) : (
                <h1 className="text-lg font-bold text-blue-200">
                  {deployment.name.includes(deployment.external_id)
                    ? deployment.name
                    : `${deployment.name} | ${deployment.external_id}`}
                </h1>
              )}
              <p className="text-blue-400">
                Start: {formatUtcDate(deployment.deployment_start_dt)}
              </p>
              <p className="text-blue-400">
                End: {formatUtcDate(deployment.deployment_end_dt)}
              </p>
              <p className="text-blue-400">
                Duration:{" "}
                {calcDateDuration(
                  deployment.deployment_start_dt,
                  deployment.deployment_end_dt
                )}
              </p>
              <p className="text-blue-400">{deployment.description}</p>
            </div>
          ))}
        </div>
      </>
    </ContentPage>
  );
}
